import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ID, ShareLinkSummary } from '@proofroom/schema';
import { share, type CreatedShareLink, type CreateShareLinkInput } from '../api/endpoints';
import { usePeople } from '../model/people';
import { Chip, ErrorState, Spinner, formatDate, timeAgo } from './ui';

export const shareLinksKey = (documentId: ID) => ['documents', documentId, 'share-links'] as const;

const EXPIRY: Array<{ value: '' | '7' | '30' | '90'; label: string }> = [
  { value: '', label: 'Never' }, { value: '7', label: '7 days' }, { value: '30', label: '30 days' }, { value: '90', label: '90 days' },
];

export interface ShareDialogProps {
  documentId: ID;
  open: boolean;
  onClose(): void;
}

/**
 * The document's share links, a form for a new one, and, once it is made, the one showing of
 * its url and password. The server gate (agency staff on the document) is the authority; the
 * Document page only shows the button to the people it would pass.
 */
export function ShareDialog({ documentId, open, onClose }: ShareDialogProps) {
  const qc = useQueryClient();
  const { nameOf } = usePeople();
  const links = useQuery({ queryKey: shareLinksKey(documentId), queryFn: () => share.list(documentId), enabled: open });
  const [created, setCreated] = useState<{ result: CreatedShareLink; password: string } | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: shareLinksKey(documentId) });
  const create = useMutation({
    mutationFn: (input: CreateShareLinkInput) => share.create(documentId, input),
    onSuccess: (result, input) => { setCreated({ result, password: input.password }); void invalidate(); },
  });
  const revoke = useMutation({ mutationFn: (id: ID) => share.revoke(id), onSuccess: () => void invalidate() });

  useEffect(() => { if (open) { setCreated(null); create.reset(); } /* eslint-disable-line react-hooks/exhaustive-deps */ }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;

  return (
    <div className="dialog-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dialog" role="dialog" aria-modal="true" aria-label="Share this document" style={{ maxWidth: 720 }}>
        <h2>Share this document</h2>
        <p className="small muted" style={{ marginBottom: 14 }}>A link with a password opens the current draft to people who are not signed in. They can read every comment, add their own and attach files; they cannot edit copy, decide changes or see earlier drafts.</p>

        {created ? (
          <CreatedPanel created={created} onDone={() => setCreated(null)} />
        ) : (
          <NewLinkForm busy={create.isPending} error={create.error} onSubmit={(input) => create.mutate(input)} />
        )}

        <h3 style={{ margin: '18px 0 8px' }}>Links</h3>
        {links.isPending && <Spinner label="Loading links" />}
        {links.isError && <ErrorState title="Could not load the links" error={links.error} retry={() => void links.refetch()} />}
        {links.data && links.data.length === 0 && <p className="small muted">No links yet.</p>}
        {links.data && links.data.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table aria-label="Share links" className="small" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--ink-3)' }}>
                  <th style={{ padding: '4px 6px' }}>Label</th><th style={{ padding: '4px 6px' }}>Created by</th><th style={{ padding: '4px 6px' }}>Created</th>
                  <th style={{ padding: '4px 6px' }}>Expires</th><th style={{ padding: '4px 6px' }}>Last used</th><th style={{ padding: '4px 6px' }}>Guests</th><th />
                </tr>
              </thead>
              <tbody>
                {links.data.map((l) => <LinkRow key={l.id} link={l} createdBy={nameOf(l.createdBy, l.createdBy.id)} revoking={revoke.isPending && revoke.variables === l.id} onRevoke={() => revoke.mutate(l.id)} />)}
              </tbody>
            </table>
          </div>
        )}
        {revoke.isError && <p className="error-text" role="alert">Could not revoke the link: {(revoke.error as Error).message}</p>}

        <div className="btn-row" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function LinkRow({ link, createdBy, revoking, onRevoke }: { link: ShareLinkSummary; createdBy: string; revoking: boolean; onRevoke(): void }) {
  const expired = !!link.expiresAt && new Date(link.expiresAt).getTime() < Date.now();
  const td = { padding: '6px', borderTop: '1px solid var(--line)', verticalAlign: 'top' as const };
  return (
    <tr>
      <td style={td}>{link.label}</td>
      <td style={td}>{createdBy}</td>
      <td style={td}>{formatDate(link.createdAt)}</td>
      <td style={td}>{link.expiresAt ? formatDate(link.expiresAt) : 'Never'}</td>
      <td style={td}>{link.lastUsedAt ? timeAgo(link.lastUsedAt) : 'Never'}</td>
      <td style={td}>{link.guestCount}</td>
      <td style={td}>
        {link.revokedAt
          ? <Chip kind="superseded">Revoked</Chip>
          : expired
            ? <Chip kind="superseded">Expired</Chip>
            : <button className="btn sm danger" disabled={revoking} onClick={onRevoke} aria-label={`Revoke ${link.label}`}>{revoking ? 'Revoking' : 'Revoke'}</button>}
      </td>
    </tr>
  );
}

function NewLinkForm({ busy, error, onSubmit }: { busy: boolean; error: unknown; onSubmit(input: CreateShareLinkInput): void }) {
  const [label, setLabel] = useState('');
  const [password, setPassword] = useState('');
  const [expiry, setExpiry] = useState<'' | '7' | '30' | '90'>('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const input: CreateShareLinkInput = { label: label.trim(), password };
    if (expiry) input.expiresInDays = Number(expiry) as 7 | 30 | 90;
    onSubmit(input);
  };
  const valid = label.trim().length > 0 && label.trim().length <= 120 && password.length >= 8;
  return (
    <form onSubmit={submit} aria-label="New link">
      <h3 style={{ marginBottom: 8 }}>New link</h3>
      <div className="btn-row" style={{ alignItems: 'flex-end' }}>
        <div className="field" style={{ flex: 2, marginBottom: 0 }}>
          <label className="label" htmlFor="share-label">Label</label>
          <input id="share-label" className="input" maxLength={120} placeholder="Who this is for" value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        <div className="field" style={{ flex: 2, marginBottom: 0 }}>
          <label className="label" htmlFor="share-password">Password (8 or more characters)</label>
          <input id="share-password" className="input" type="text" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className="field" style={{ flex: 1, marginBottom: 0 }}>
          <label className="label" htmlFor="share-expiry">Expires</label>
          <select id="share-expiry" className="input" value={expiry} onChange={(e) => setExpiry(e.target.value as '' | '7' | '30' | '90')}>
            {EXPIRY.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <button className="btn primary" type="submit" disabled={!valid || busy}>{busy ? 'Creating' : 'Create link'}</button>
      </div>
      {error ? <p className="error-text" role="alert" style={{ marginTop: 8 }}>{error instanceof Error ? error.message : 'Could not create the link'}</p> : null}
    </form>
  );
}

function CopyField({ id, label, value }: { id: string; label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* the field is selectable by hand */ }
  };
  return (
    <div className="field" style={{ marginBottom: 8 }}>
      <label className="label" htmlFor={id}>{label}</label>
      <div className="btn-row" style={{ flexWrap: 'nowrap' }}>
        <input id={id} className="input mono" readOnly value={value} onFocus={(e) => e.target.select()} />
        <button type="button" className="btn" onClick={() => void copy()}>{copied ? 'Copied' : 'Copy'}</button>
      </div>
    </div>
  );
}

/** Shown once: the url and the password are not kept anywhere they can be read back from. */
function CreatedPanel({ created, onDone }: { created: { result: CreatedShareLink; password: string }; onDone(): void }) {
  return (
    <div className="card" style={{ padding: 14, background: 'var(--bg)' }}>
      <h3 style={{ marginBottom: 8 }}>Link created: {created.result.link.label}</h3>
      <CopyField id="share-url" label="Link" value={created.result.url} />
      <CopyField id="share-created-password" label="Password" value={created.password} />
      <p className="small muted" role="note">These are not shown again. Copy them now, and send the password separately from the link.</p>
      <div className="btn-row" style={{ marginTop: 8 }}>
        <button type="button" className="btn sm" onClick={onDone}>Done</button>
      </div>
    </div>
  );
}
